import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { API_URL } from "../../lib/utils";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Check, Loader2, X, ArrowRight } from "lucide-react";

export const SubscriptionSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const sessionId = searchParams.get("session_id");
    const [status, setStatus] = useState("polling");
    const [attempts, setAttempts] = useState(0);
    const processedRef = useRef(false);
    const maxAttempts = 8;

    useEffect(() => {
        if (!sessionId || !token || processedRef.current) return;

        const poll = async () => {
            if (processedRef.current) return;
            try {
                const res = await fetch(`${API_URL}/subscription/checkout/status/${sessionId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error("Failed to check status");
                const data = await res.json();

                if (data.payment_status === "paid") {
                    processedRef.current = true;
                    setStatus("success");
                    return;
                }
                if (data.status === "expired") {
                    processedRef.current = true;
                    setStatus("expired");
                    return;
                }

                setAttempts((prev) => {
                    if (prev + 1 >= maxAttempts) {
                        setStatus("timeout");
                        return prev + 1;
                    }
                    setTimeout(poll, 2500);
                    return prev + 1;
                });
            } catch {
                setAttempts((prev) => {
                    if (prev + 1 >= maxAttempts) {
                        setStatus("error");
                        return prev + 1;
                    }
                    setTimeout(poll, 2500);
                    return prev + 1;
                });
            }
        };

        poll();
    }, [sessionId, token]);

    const renderContent = () => {
        switch (status) {
            case "polling":
                return (
                    <>
                        <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto" />
                        <h2 className="text-xl font-bold mt-6">Verification du paiement...</h2>
                        <p className="text-muted-foreground mt-2">
                            Tentative {attempts + 1}/{maxAttempts}
                        </p>
                    </>
                );
            case "success":
                return (
                    <>
                        <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
                            <Check className="w-8 h-8 text-green-600" />
                        </div>
                        <h2 className="text-xl font-bold mt-6 text-green-600">Paiement reussi !</h2>
                        <p className="text-muted-foreground mt-2">
                            Votre abonnement est maintenant actif.
                        </p>
                        <Button className="mt-6" onClick={() => navigate("/settings/subscription")} data-testid="go-to-subscription-btn">
                            <ArrowRight className="w-4 h-4 mr-2" />
                            Voir mon abonnement
                        </Button>
                    </>
                );
            case "expired":
            case "timeout":
            case "error":
                return (
                    <>
                        <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto">
                            <X className="w-8 h-8 text-red-600" />
                        </div>
                        <h2 className="text-xl font-bold mt-6 text-red-600">
                            {status === "expired" ? "Session expiree" : "Erreur de verification"}
                        </h2>
                        <p className="text-muted-foreground mt-2">
                            Verifiez votre email pour confirmation ou reessayez.
                        </p>
                        <Button className="mt-6" variant="outline" onClick={() => navigate("/settings/subscription")} data-testid="go-back-btn">
                            Retour aux abonnements
                        </Button>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex items-center justify-center min-h-[60vh]" data-testid="subscription-success-page">
            <Card className="max-w-md w-full">
                <CardContent className="pt-8 pb-8 text-center">
                    {renderContent()}
                </CardContent>
            </Card>
        </div>
    );
};
